<script setup lang="ts">
// Segmented control for the Reminders card (One-time / Schedules, and the
// Pending / Done / All filter): a real tablist with a roving tab stop, so
// arrow keys move between segments and Tab leaves the group in one press.
export interface SegmentOption {
  label: string
  value: string
}

const props = defineProps<{
  modelValue: string
  options: SegmentOption[]
  label: string
  controls?: string
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

function select(value: string): void {
  if (value !== props.modelValue) emit('update:modelValue', value)
}

// Arrow keys wrap around the ends, the WAI-ARIA tabs pattern with automatic
// activation: focusing a segment selects it.
function onKeydown(event: KeyboardEvent, index: number): void {
  const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
  if (step === 0) return
  event.preventDefault()
  const next = (index + step + props.options.length) % props.options.length
  select(props.options[next].value)
  const group = (event.currentTarget as HTMLElement).parentElement
  group?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
}
</script>

<template>
  <div
    role="tablist"
    :aria-label="label"
    class="flex gap-1 rounded-[10px] border border-border-subtle bg-surface-bg p-[3px]"
  >
    <button
      v-for="(opt, i) in options"
      :key="opt.value"
      type="button"
      role="tab"
      :aria-selected="opt.value === modelValue"
      :aria-controls="controls"
      :tabindex="opt.value === modelValue ? 0 : -1"
      class="min-h-11 rounded-[7px] px-3 text-[13px] transition-colors sm:min-h-8"
      :class="opt.value === modelValue ? 'bg-surface-hover font-semibold text-ink' : 'text-ink-muted hover:text-ink'"
      @click="select(opt.value)"
      @keydown="onKeydown($event, i)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>
